import { useState } from 'react';
import EntryChip from './EntryChip';
import EntryTimeRangePopover from './EntryTimeRangePopover';
import { formatEntryTimeLabel } from './dateSetUtils';
import { ALL_DAY_WINDOW, EntryTimeWindow } from './dateSetTypes';
import styles from './ChipList.module.css';

interface EntryChipListProps<T extends { window: EntryTimeWindow }> {
  entries: T[];
  /** A stable identity for an entry (its date, its day). */
  getKey: (entry: T) => string | number;
  /** Display order of the chips, independent of the order entries were added. */
  compare: (a: T, b: T) => number;
  /** The chip's date/day label. */
  getLabel: (entry: T) => string;
  onChange: (entries: T[]) => void;
  disabled?: boolean;
}

/** Selected dates or days as chips, each with its own time-range popover.
 * Only one chip's popover is open at a time. */
function EntryChipList<T extends { window: EntryTimeWindow }>({
  entries,
  getKey,
  compare,
  getLabel,
  onChange,
  disabled = false,
}: EntryChipListProps<T>) {
  const [openKey, setOpenKey] = useState<string | number | null>(null);

  if (entries.length === 0) return null;

  const updateWindow = (key: string | number, window: EntryTimeWindow) => {
    onChange(entries.map((e) => (getKey(e) === key ? { ...e, window } : e)));
  };

  return (
    <div className={styles.list}>
      {[...entries].sort(compare).map((entry) => {
        const key = getKey(entry);
        return (
          <EntryTimeRangePopover
            key={key}
            window={entry.window}
            open={openKey === key}
            onOpenChange={(open) => setOpenKey(open ? key : null)}
            onSave={(window) => updateWindow(key, window)}
            onClear={() => updateWindow(key, ALL_DAY_WINDOW)}
          >
            <EntryChip
              primaryLabel={getLabel(entry)}
              timeLabel={formatEntryTimeLabel(entry.window)}
              onOpenTimeRange={() => setOpenKey(key)}
              onRemove={() =>
                onChange(entries.filter((e) => getKey(e) !== key))
              }
              active={openKey === key}
              disabled={disabled}
            />
          </EntryTimeRangePopover>
        );
      })}
    </div>
  );
}

export default EntryChipList;
